import { Box, Paper, Typography, styled } from "@mui/material";

export const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  height: "100vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
}));

export const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "24px",
  color: "#1a1a1a",
}));

// Styled components for Search Input Wrapper
export const SearchWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 12px',
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  backgroundColor: '#fff',
  width: '240px', // Matches standardized width
  height: '36px', // Matches other filter heights
}));

export const StyledInput = styled("input")(({ theme }) => ({
  border: 'none',
  outline: 'none',
  width: '100%',
  fontSize: '14px',
  '&::placeholder': {
    color: '#9ca3af',
  }
}));


export const BoardContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 7,
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  flex: 1,
  display: "flex",
  flexDirection: "column",
}));

// ... FiltersContainer, DateNavigator, DateText remain similar ...
export const FiltersContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between", // Date Left, Filters Right
  alignItems: "center",
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

export const DateNavigator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const DateText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "14px", // Reduced from 15px
  color: "#1a1a1a",
  lineHeight: 1,
}));

export const MonitoringGrid = styled(Box)<{ daysCount?: number }>(({ theme, daysCount = 7 }) => ({
  display: "grid",
  gridTemplateColumns: `60px repeat(${daysCount}, minmax(140px, 1fr))`,
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#fff",
  flex: 1,
  overflowY: "auto",
  overflowX: "auto",
  position: "relative",
  gridAutoRows: "min-content",
  alignItems: "stretch",
}));

export const GridHeaderCell = styled(Box)(({ theme }) => ({
  backgroundColor: "#447068",
  color: "#ffffff",
  padding: theme.spacing(1),
  textAlign: "center",
  fontWeight: 600,
  fontSize: "13px",
  borderRight: "1px solid rgba(255,255,255,0.2)",
  "&:last-child": {
    borderRight: "none",
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

export const CornerHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#447068",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRight: "1px solid rgba(255,255,255,0.2)",
  position: "sticky",
  top: 0,
  left: 0,
  zIndex: 15,
}));

export const TimeCell = styled(Box)(({ theme }) => ({
  backgroundColor: "#f9fafb",
  padding: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  color: "#9ca3af",
  fontSize: "11px",
  borderBottom: "1px dashed #e5e7eb",
  borderRight: "1px solid #e5e7eb",
  position: "sticky",
  left: 0,
  zIndex: 5,
  height: "100%",
}));

export const GridCell = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  minHeight: "80px", 
  padding: theme.spacing(1),
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.spacing(1),
  borderBottom: "1px dashed #526f68",
  borderRight: "1px solid #526f68",
  height: "100%",
  minWidth: 0,
}));

export const ShiftCard = styled(Box)<{ status: string }>(({ theme, status }) => {
  let borderColor = "rgba(255, 255, 255, 0.1)";
  let background = "rgba(17, 24, 39, 0.9)"; // Neutral dark

  // Dark Glass Colors (Rich, bold backgrounds with white text)
  switch (status) {
    case "completed":
    case "completed_green":
    case "partially_completed":
      // Primary Green #479577
      background = "#479577";
      borderColor = "rgba(71, 149, 119, 0.5)";
      break;
    case "ongoing":
    case "started":
    case "clocked_in":
    case "partially_started":
      // Light Green (User requested greenish)
      background = "#66bb6a";
      borderColor = "rgba(102, 187, 106, 0.5)";
      break;
    case "upcoming":
    case "scheduled":
    case "assigned":
      // Link Blue #4A90E2
      background = "#4A90E2";
      borderColor = "rgba(74, 144, 226, 0.5)";
      break;
    case "unassigned":
    case "partially_assigned":
      // Warning Gold #D2A627
      background = "#D2A627";
      borderColor = "rgba(210, 166, 39, 0.5)";
      break;
    case "cancelled":
    case "canceled":
      // Error Red #FF4D4F
      background = "#FF4D4F";
      borderColor = "rgba(255, 77, 79, 0.5)";
      break;
    case "absent":
    case "partially_absent":
      // Orange for Absent
      background = "#FF9800";
      borderColor = "rgba(255, 152, 0, 0.5)";
      break;
    case "facility_approval_pending":
    case "partially_approval_pending":
    case "approval_pending":
      // Brownish for Approval Pending
      background = "#8D6E63";
      borderColor = "rgba(141, 110, 99, 0.5)";
      break;
    case "facility_reject":
    case "rejected":
    case "partially_rejected":
      // Distinct Red for Facility Reject
      background = "#C62828";
      borderColor = "rgba(198, 40, 40, 0.5)";
      break;
    default:
      background = "rgba(31, 41, 55, 0.9)";
  }

  return {
    background: background,
    color: "#ffffff", // White text for all dark cards
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",

    border: `1px solid ${borderColor}`,
    borderRadius: "8px", // Reduced from 12px
    padding: theme.spacing(1), // Reduced from 1.5

    // Deep shadow for pop
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0,0,0,0.1)",

    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    marginBottom: "0px", 
    width: "100%",
    minWidth: "120px",

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0,0,0,0.2)",
      borderColor: "rgba(255, 255, 255, 0.4)", // Brighten border on hover
    },
  };
});

export const CardTime = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "11px", // Reduced from 13px
  color: "#ffffff",
  marginBottom: "6px", // Reduced from 8px
  // Semi-transparent white overlay on dark bg
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  border: "1px solid rgba(255,255,255,0.1)",
  display: 'inline-block',
  borderRadius: '4px',
  padding: '2px 6px', // Reduced from 4px 8px
}));

export const CardRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px", // Reduced
  marginBottom: "2px", // Reduced
  opacity: 0.9,
}));

export const CardText = styled(Typography)(({ theme }) => ({
  fontSize: "11px", // Reduced from 12px
  color: "inherit",
  lineHeight: 1.3,
  fontWeight: 500,
}));

export const LegendContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

export const LegendItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const LegendColor = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "2px",
  backgroundColor: color,
}));

export const LegendLabel = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  color: "#4b5563",
  fontWeight: 500,
}));
